/**
 * Tenant Settings Hook
 *
 * Fetches tenant settings and provides business hours
 * in the format expected by the calendar components.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { SchedulingType, TenantFeatures, WorkDurationStepMinutes, WorkshopOpeningHours } from '@/types';
import { DEFAULT_WORK_DURATION_STEP_MINUTES } from '@/utils/duration';

/** Workshop-managed manufacturer service-book URLs (#2129). Portal-only. */
export interface ServiceBookLink {
  name: string;
  url: string;
  note?: string;
}

/** Workshop-defined reception questions asked at work-order create (#2130). */
export interface AdditionalQuestion {
  id: string;
  label: string;
  isActive: boolean;
}

/**
 * Workshop-wide idle lock / logout (#2216). Same numbers as staff-api
 * `tenant.schema` — duplicated because the portal cannot import that package.
 *
 * Logout max 600 stays under the portal's 12 h refresh lifetime.
 */
export const DEFAULT_IDLE_LOCK_MINUTES = 15;
export const DEFAULT_IDLE_LOGOUT_MINUTES = 480;
export const IDLE_LOCK_MINUTES_MIN = 1;
export const IDLE_LOCK_MINUTES_MAX = 240;
export const IDLE_LOGOUT_MINUTES_MIN = 2;
export const IDLE_LOGOUT_MINUTES_MAX = 600;

export interface TenantSecuritySettings {
  idleLockMinutes?: number;
  idleLogoutMinutes?: number;
}

export interface TenantIdleTimeoutSettings {
  idleLockMinutes: number;
  idleLogoutMinutes: number;
}

function isConfiguredIdleMinutes(value: unknown, min: number, max: number): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
}

/**
 * Reader for `settings.security`. Missing section, missing half, inverted pair,
 * non-numbers, non-finite values, non-integers, or out-of-range minutes all
 * resolve to the documented defaults so a tenant without a valid key behaves
 * exactly like 15 / 480.
 */
export function resolveIdleTimeoutSettings(
  security:
    | { readonly idleLockMinutes?: unknown; readonly idleLogoutMinutes?: unknown }
    | null
    | undefined,
): TenantIdleTimeoutSettings {
  const idleLockMinutes = security?.idleLockMinutes ?? DEFAULT_IDLE_LOCK_MINUTES;
  const idleLogoutMinutes = security?.idleLogoutMinutes ?? DEFAULT_IDLE_LOGOUT_MINUTES;
  if (
    isConfiguredIdleMinutes(idleLockMinutes, IDLE_LOCK_MINUTES_MIN, IDLE_LOCK_MINUTES_MAX) &&
    isConfiguredIdleMinutes(idleLogoutMinutes, IDLE_LOGOUT_MINUTES_MIN, IDLE_LOGOUT_MINUTES_MAX) &&
    idleLogoutMinutes > idleLockMinutes
  ) {
    return { idleLockMinutes, idleLogoutMinutes };
  }
  return {
    idleLockMinutes: DEFAULT_IDLE_LOCK_MINUTES,
    idleLogoutMinutes: DEFAULT_IDLE_LOGOUT_MINUTES,
  };
}

interface TenantSettingsResponse {
  id: string;
  name: string;
  /** Effective WhatsApp inbox switch: config exists and the flag is not off (#1475). */
  whatsappAvailable?: boolean;
  /** Workshop mailbox copy switched on for this tenant (#3736). */
  mailboxInboxEnabled?: boolean;
  settings: {
    businessHours?: WorkshopOpeningHours;
    hoursExceptions?: {
      id: string;
      date: string;
      isClosed: boolean;
      open?: string;
      close?: string;
      reason?: string;
    }[];
    timezone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    contactPhone?: string;
    contactEmail?: string;
    businessId?: string;
    /** Workshop-wide default (cents) for the work-order pre-approved additional-work limit (#296). */
    defaultAdditionalWorkLimitCents?: number;
    /** Scheduling type pre-selected for a new work order (#1931). */
    defaultSchedulingType?: SchedulingType;
    /** Minutes added to work duration when suggesting a pickup time (#2117). */
    defaultPickupBufferMinutes?: number;
    /** Step for work-duration sliders and arrows (#3091). */
    workDurationStepMinutes?: WorkDurationStepMinutes;
    /** Workshop-managed manufacturer service-book URLs (#2129). */
    serviceBookLinks?: ServiceBookLink[];
    /** Workshop-defined reception questions asked at work-order create (#2130). */
    additionalQuestions?: AdditionalQuestion[];
    /** Workshop-wide default validity (days) for new quotations (#849). */
    defaultQuoteValidityDays?: number;
    /** Unpaid lunch break (minutes) subtracted from each shift day's calendar capacity (#423). */
    workdayUnpaidLunchMinutes?: number;
    /** Workshop-wide idle lock / logout (#2216). */
    security?: TenantSecuritySettings;
    /** Feature / module flags (#2646). Missing module keys stay visible. */
    features?: TenantFeatures;
    [key: string]: unknown;
  };
}

/**
 * Workshop module visibility (#2646). Explicit `false` hides the module;
 * missing, `true`, or an unloaded `/tenants/me` stays visible so existing
 * tenants do not flash a missing nav item.
 */
export function isTenantModuleEnabled(value: unknown): boolean {
  return value !== false;
}

/** Calendar-compatible hours: ISO weekday (1=Mon) → time slots */
export type CalendarHours = Record<number, { start: string; end: string }[]>;

const defaultBusinessHours: WorkshopOpeningHours = {
  monday: { open: '08:00', close: '17:00' },
  tuesday: { open: '08:00', close: '17:00' },
  wednesday: { open: '08:00', close: '17:00' },
  thursday: { open: '08:00', close: '17:00' },
  friday: { open: '08:00', close: '17:00' },
  saturday: null,
  sunday: null,
};

const dayToIsoWeekday: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/**
 * Converts named-day business hours to the ISO-weekday-keyed
 * format used across the app's calendar utils.
 */
export function toCalendarHours(hours: WorkshopOpeningHours): CalendarHours {
  const result: CalendarHours = {};
  for (const [day, isoDay] of Object.entries(dayToIsoWeekday)) {
    const dayHours = hours[day as keyof WorkshopOpeningHours];
    if (dayHours) {
      result[isoDay] = [{ start: dayHours.open, end: dayHours.close }];
    }
  }
  return result;
}

/**
 * Re-keys ISO weekday hours (1 = Mon … 7 = Sun) to the JS `Date.getDay()`
 * convention (0 = Sun … 6 = Sat) that `mq-timeline-calendar` uses when it
 * reads `availability.weekly`.
 *
 * Mon–Sat happen to line up, so only Sunday moves — but without the move the
 * library never finds it: the horizontal timeline paints an open Sunday as
 * fully unavailable, and the vertical day view falls back to a 00–24 axis
 * (#1250). Always pass hours through this before handing them to a calendar.
 */
export function toAvailabilityWeekly(hours: CalendarHours): CalendarHours {
  const result: CalendarHours = {};
  for (const [key, slots] of Object.entries(hours)) {
    const isoDay = Number(key);
    result[isoDay === 7 ? 0 : isoDay] = slots;
  }
  return result;
}

/**
 * Hook that fetches tenant settings and returns business hours
 * in the calendar-compatible format.
 */
export function useTenantSettings() {
  const query = useQuery({
    queryKey: queryKeys.tenant.settings(),
    queryFn: () => api.get<TenantSettingsResponse>('/tenants/me'),
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
  });

  const businessHours = query.data?.settings.businessHours ?? defaultBusinessHours;
  const idleTimeout = resolveIdleTimeoutSettings(query.data?.settings.security);
  const features = query.data?.settings.features;

  const calendarHours = useMemo(
    () => toCalendarHours(businessHours),
    [businessHours],
  );

  return {
    ...query,
    tenantName: query.data?.name ?? '',
    tenantSettings: query.data?.settings,
    idleLockMinutes: idleTimeout.idleLockMinutes,
    idleLogoutMinutes: idleTimeout.idleLogoutMinutes,
    // While /tenants/me is loading, keep the inbox visible so configured
    // workshops do not flash a missing nav item. Hide only after a definite false.
    whatsappAvailable: query.data?.whatsappAvailable !== false,
    // Hidden until a definite true: most workshops never route their mailbox here.
    mailboxInboxEnabled: query.data?.mailboxInboxEnabled === true,
    inspectionsEnabled: isTenantModuleEnabled(features?.inspectionsEnabled),
    serviceBookEnabled: isTenantModuleEnabled(features?.serviceBookEnabled),
    partsOrderingEnabled: isTenantModuleEnabled(features?.partsOrderingEnabled),
    businessHours,
    calendarHours,
    schedulingLookaheadDays: (query.data?.settings.schedulingLookaheadDays as number | undefined) ?? 4,
    // 'floating' when the workshop has never set it: most work is done during
    // the day, an exact time is the exception (#1931). Resolved here rather
    // than by a JSONB migration, so existing tenants need no data change.
    defaultSchedulingType:
      (query.data?.settings.defaultSchedulingType as SchedulingType | undefined) ?? 'floating',
    defaultPickupBufferMinutes:
      (query.data?.settings.defaultPickupBufferMinutes as number | undefined) ?? 60,
    // 30 min when the workshop has never chosen: the step the duration fields
    // used before the setting existed (#3091). Reader default, no migration.
    workDurationStepMinutes:
      (query.data?.settings.workDurationStepMinutes as WorkDurationStepMinutes | undefined)
      ?? DEFAULT_WORK_DURATION_STEP_MINUTES,
    serviceBookLinks: (query.data?.settings.serviceBookLinks as ServiceBookLink[] | undefined) ?? [],
    additionalQuestions: (query.data?.settings.additionalQuestions as AdditionalQuestion[] | undefined) ?? [],
    defaultQuoteValidityDays: (query.data?.settings.defaultQuoteValidityDays as number | undefined) ?? 14,
    workdayUnpaidLunchMinutes: (query.data?.settings.workdayUnpaidLunchMinutes as number | undefined) ?? 30,
  };
}

/** Workshop info derived from tenant settings, for reports/print views */
export interface WorkshopInfo {
  workshopName: string;
  workshopAddress: string;
  workshopPhone: string;
  workshopEmail: string;
  businessId: string;
}

/**
 * Hook that returns workshop contact info from tenant settings.
 * Use in report/print views instead of hardcoded values.
 */
export function useWorkshopInfo(): WorkshopInfo {
  const { tenantName, tenantSettings } = useTenantSettings();
  const s = tenantSettings;
  const parts = [s?.address, s?.postalCode && s?.city ? `${s.postalCode} ${s.city}` : s?.city].filter(Boolean);
  return {
    workshopName: tenantName,
    workshopAddress: parts.join(', '),
    workshopPhone: s?.contactPhone ?? '',
    workshopEmail: s?.contactEmail ?? '',
    businessId: s?.businessId ?? '',
  };
}

/**
 * The workshop floor-plan image, on its own endpoint.
 *
 * It is a data: URL of up to a few megabytes, so it is deliberately kept out
 * of `/tenants/me` (which every page fetches). It used to be PATCHed into
 * `/tenants/me/settings`, where the server's Zod schema silently dropped it —
 * the upload reported nothing and saved nothing (#1797).
 */
export function useFloorPlan() {
  const query = useQuery({
    queryKey: queryKeys.tenant.floorPlan(),
    queryFn: () => api.get<{ url: string | null }>('/tenants/me/floor-plan'),
    staleTime: 5 * 60 * 1000,
  });

  return { ...query, floorPlanUrl: query.data?.url ?? undefined };
}

export function useUpdateFloorPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (url: string | null) =>
      api.put<{ url: string | null }>('/tenants/me/floor-plan', { url }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.tenant.floorPlan(), data);
    },
  });
}

/**
 * Hook to update tenant settings
 */
export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.patch('/tenants/me/settings', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tenant.settings(),
      });
    },
  });
}
